import React, { useState } from "react"
import PropTypes from "prop-types"
import {
  Col,
  Row,
  Card,
  CardBody,
  Label,
  Form,
  Input,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap"
import moment from "moment"

const readOnlyBoxStyle = {
  backgroundColor: "#e9ecef",
  border: "1px solid #ced4da",
  borderRadius: "6px",
  padding: "0.5rem 0.75rem",
  minHeight: "38px",
}

/** ระยะห่างระหว่าง label กับ value (ประมาณ 1 tab + เล็กน้อย) */
const labelValueGap = "5rem"

const UIDS_PER_PAGE = 10

const Step2CampaignDetails = ({
  validation,
  pageView,
  surveyName,
  categoryOptions = [],
  lineOaOptions = [],
}) => {
  const values = validation.values || {}
  const linkOnly = values.broadcastTimeType === "none"
  const [showUidModal, setShowUidModal] = useState(false)
  const [uidPage, setUidPage] = useState(1)

  const selectedLineOaName = React.useMemo(() => {
    const id = values.lineOaId
    if (!id) return ""
    const oa = lineOaOptions.find(o => String(o.id) === String(id))
    return oa ? oa.oa_name : ""
  }, [values.lineOaId, lineOaOptions])

  const selectedCategoryName = React.useMemo(() => {
    const id = values.categoryId
    if (!id) return ""
    const cat = categoryOptions.find(c => String(c.id) === String(id))
    return cat ? cat.category_name : ""
  }, [values.categoryId, categoryOptions])

  const contentBlocks = values.contentBlocks || []
  const isImageType = type => type === "image" || type === "pictures"
  const recipientsLabel = values.recipients === "targeting" ? "Targeting" : "All friends"
  const audienceGender = values.audienceGender || []
  const audienceAge = values.audienceAge || []
  const audienceOs = values.audienceOs || []
  const audienceFriendship = values.audienceFriendshipPeriod || []
  const audienceCity = values.audienceCity || []
  const broadcastTimeText =
    values.broadcastTimeType === "schedule" && (values.scheduleDate || values.scheduleTime)
      ? `${values.scheduleDate || ""} ${values.scheduleTime || ""} (UTC +7:00)`.trim()
      : values.broadcastTimeType === "now"
        ? "Send now"
        : "-"

  return (
    <>
      <Row>
        <Col>
          <Card style={{ borderRadius: "8px", overflow: "hidden" }}>
            <div
              className="p-3"
              style={{
                backgroundColor: "#E6F8F3",
                color: "#1a5d3a",
                fontWeight: "600",
                fontSize: "1.1rem",
                borderTopLeftRadius: "8px",
                borderTopRightRadius: "8px",
              }}
            >
              Preview Campaign
            </div>
            <CardBody>
              <Form>
                {/* LINE OA Selection */}
                <div className="mb-4">
                  <div className="mb-2 fw-bold" style={{ fontSize: "1rem", color: "#495057" }}>
                    LINE OA Selection
                  </div>
                  <Row className="mb-2">
                    <Label className="col-md-3 col-form-label fw-semibold">LINE OA</Label>
                    <Col md={5}>
                      <div style={readOnlyBoxStyle}>{selectedLineOaName || "-"}</div>
                    </Col>
                  </Row>
                </div>

                {/* Survey Selection */}
                <div className="mb-4" style={{ borderTop: "1px solid #ced4da", paddingTop: "1rem" }}>
                  <div className="mb-2 fw-bold" style={{ fontSize: "1rem", color: "#495057" }}>
                    Survey Selection
                  </div>
                  <Row className="mb-2">
                    <Label className="col-md-3 col-form-label fw-semibold">Category</Label>
                    <Col md={5}>
                      <div style={readOnlyBoxStyle}>{selectedCategoryName || "-"}</div>
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Label className="col-md-3 col-form-label fw-semibold">Survey Name</Label>
                    <Col md={5}>
                      <div style={readOnlyBoxStyle}>{surveyName || "-"}</div>
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Label className="col-md-3 col-form-label fw-semibold">Survey Line URL</Label>
                    <Col md={5}>
                      <div className="d-flex gap-2 align-items-center">
                        <div style={{ ...readOnlyBoxStyle, flex: 1, wordBreak: "break-all" }}>{values.surveyUrl || "-"}</div>
                        {values.surveyUrl && (
                          <Button
                            type="button"
                            color="secondary"
                            outline
                            size="sm"
                            disabled={pageView === "create"}
                            onClick={() => {
                              navigator.clipboard.writeText(values.surveyUrl)
                              alert("Survey Line URL copied to clipboard!")
                            }}
                            style={{ whiteSpace: "nowrap" }}
                          >
                            <i className="bx bx-copy me-1"></i>
                            Copy
                          </Button>
                        )}
                      </div>
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Label className="col-md-3 col-form-label fw-semibold">Survey Mobile URL</Label>
                    <Col md={5}>
                      <div className="d-flex gap-2 align-items-center">
                        <div style={{ ...readOnlyBoxStyle, flex: 1, wordBreak: "break-all" }}>{values.surveyMobileUrl || "-"}</div>
                        {values.surveyMobileUrl && (
                          <Button
                            type="button"
                            color="secondary"
                            outline
                            size="sm"
                            disabled={pageView === "create"}
                            onClick={() => {
                              navigator.clipboard.writeText(values.surveyMobileUrl)
                              alert("Survey Mobile URL copied to clipboard!")
                            }}
                            style={{ whiteSpace: "nowrap" }}
                          >
                            <i className="bx bx-copy me-1"></i>
                            Copy
                          </Button>
                        )}
                      </div>
                    </Col>
                  </Row>
                  {pageView === "create" && <p className="text-muted small">Save the campaign first to get its final link.</p>}
                  {linkOnly && <p className="text-muted small">If this campaign offers both LINE MAN and Grab rewards, use the matching entry link from Coupons &amp; Vouchers for your banner.</p>}
                </div>

                {/* Specific User (only when targeting) */}
                {!linkOnly && values.recipients === "targeting" && values.specificUserFileName && (
                  <div className="mb-4 pt-3" style={{ borderTop: "1px solid #ced4da" }}>
                    <div className="mb-2 fw-bold" style={{ fontSize: "1rem", color: "#495057" }}>
                      Specific User
                    </div>
                    <Row className="mb-2 align-items-center">
                      <Col md={6}>
                        <div className="d-flex align-items-center gap-2">
                          <Input
                            type="text"
                            value={values.specificUserFileName || ""}
                            disabled
                            style={{ backgroundColor: "#e9ecef" }}
                          />
                          <Button
                            type="button"
                            outline
                            color="secondary"
                            style={{ whiteSpace: "nowrap", borderRadius: "6px" }}
                            onClick={() => { setUidPage(1); setShowUidModal(true) }}
                          >
                            <i className="bx bx-show me-1"></i>
                            View
                          </Button>
                        </div>
                      </Col>
                    </Row>
                  </div>
                )}

                {/* Broadcast Settings */}
                {linkOnly ? (
                  <div className="mb-4 pt-3" style={{ borderTop: "1px solid #ced4da" }}>
                    <div className="mb-2 fw-bold" style={{ fontSize: "1rem", color: "#495057" }}>Campaign Delivery</div>
                    <p className="fw-semibold mb-2">Publish without sending</p>
                    <p className="text-muted mb-0">{pageView === "view"
                      ? values.status === "publish" ? "This campaign is published. No LINE broadcast was sent. Copy its link to use in your banner." : "No LINE broadcast is configured for this campaign."
                      : "This campaign will be published without sending a LINE message. Copy its link after publishing."}</p>
                  </div>
                ) : (
                <div className="mb-4 pt-3" style={{ borderTop: "1px solid #ced4da" }}>
                  <div className="mb-2 fw-bold" style={{ fontSize: "1rem", color: "#495057" }}>
                    Broadcast Settings
                  </div>
                  {/* Recipients = หัวข้อใหญ่ ไม่ให้คำขึ้นบรรทัดใหม่, Col แรก 120px ทุกแถว */}
                  <Row className="mb-3 align-items-center g-0">
                    <Col xs="12" md="auto" style={{ width: "120px", minWidth: "120px", flex: "0 0 120px", paddingRight: labelValueGap }}>
                      <div className="fw-bold" style={{ fontSize: "1rem", whiteSpace: "nowrap" }}>
                        Recipients:
                      </div>
                    </Col>
                    <Col style={{ paddingLeft: labelValueGap }}>
                      <span>{recipientsLabel}</span>
                    </Col>
                  </Row>
                  <Row className="g-0 mb-3">
                    <Col xs="12" md="auto" style={{ width: "120px", minWidth: "120px", flex: "0 0 120px", paddingRight: labelValueGap }} />
                    <Col style={{ paddingLeft: labelValueGap }}>
                      <hr className="my-0" style={{ borderColor: "#ced4da" }} />
                    </Col>
                  </Row>
                  {values.recipients !== "all" && (
                    <>
                      <Row className="mb-3 align-items-center g-0">
                        <Col xs="12" md="auto" style={{ width: "120px", minWidth: "120px", flex: "0 0 120px", paddingRight: labelValueGap }} />
                        <Col style={{ paddingLeft: labelValueGap }}>
                          <div className="d-flex flex-column gap-1">
                            <Label className="col-form-label mb-0">Recipients label (optional)</Label>
                            <Input
                              type="text"
                              name="recipientsLabel"
                              value={values.recipientsLabel || ""}
                              disabled
                              style={{ maxWidth: "100%", backgroundColor: "#e9ecef" }}
                            />
                          </div>
                        </Col>
                      </Row>
                    </>
                  )}
                  {values.recipients !== "all" && (
                    <>
                      <Row className="mb-3 align-items-start g-0">
                        <Col xs="12" md="auto" style={{ width: "120px", minWidth: "120px", flex: "0 0 120px", paddingRight: labelValueGap }} />
                        <Col style={{ paddingLeft: labelValueGap }}>
                          <div className="d-flex" style={{ minHeight: "1.5rem" }}>
                            <span className="fw-semibold text-nowrap" style={{ minWidth: "180px", paddingRight: labelValueGap }}>Gender:</span>
                            <span>
                              {audienceGender.length === 0 ? "-" : audienceGender.map((v, i) => <div key={i}>{v}</div>)}
                            </span>
                          </div>
                        </Col>
                      </Row>
                      <Row className="mb-3 align-items-start g-0">
                        <Col xs="12" md="auto" style={{ width: "120px", minWidth: "120px", flex: "0 0 120px", paddingRight: labelValueGap }} />
                        <Col style={{ paddingLeft: labelValueGap }}>
                          <div className="d-flex" style={{ minHeight: "1.5rem" }}>
                            <span className="fw-semibold text-nowrap" style={{ minWidth: "180px", paddingRight: labelValueGap }}>Age:</span>
                            <span>
                              {audienceAge.length === 0 ? "-" : audienceAge.map((v, i) => <div key={i}>{v}</div>)}
                            </span>
                          </div>
                        </Col>
                      </Row>
                      <Row className="mb-3 align-items-start g-0">
                        <Col xs="12" md="auto" style={{ width: "120px", minWidth: "120px", flex: "0 0 120px", paddingRight: labelValueGap }} />
                        <Col style={{ paddingLeft: labelValueGap }}>
                          <div className="d-flex" style={{ minHeight: "1.5rem" }}>
                            <span className="fw-semibold text-nowrap" style={{ minWidth: "180px", paddingRight: labelValueGap }}>Operating System:</span>
                            <span>
                              {audienceOs.length === 0 ? "-" : audienceOs.map((v, i) => <div key={i}>{v}</div>)}
                            </span>
                          </div>
                        </Col>
                      </Row>
                      <Row className="mb-3 align-items-start g-0">
                        <Col xs="12" md="auto" style={{ width: "120px", minWidth: "120px", flex: "0 0 120px", paddingRight: labelValueGap }} />
                        <Col style={{ paddingLeft: labelValueGap }}>
                          <div className="d-flex" style={{ minHeight: "1.5rem" }}>
                            <span className="fw-semibold text-nowrap" style={{ minWidth: "180px", paddingRight: labelValueGap }}>Friendship Period:</span>
                            <span>
                              {audienceFriendship.length === 0 ? "-" : audienceFriendship.map((v, i) => <div key={i}>{v}</div>)}
                            </span>
                          </div>
                        </Col>
                      </Row>
                      <Row className="mb-3 align-items-start g-0">
                        <Col xs="12" md="auto" style={{ width: "120px", minWidth: "120px", flex: "0 0 120px", paddingRight: labelValueGap }} />
                        <Col style={{ paddingLeft: labelValueGap }}>
                          <div className="d-flex" style={{ minHeight: "1.5rem" }}>
                            <span className="fw-semibold text-nowrap" style={{ minWidth: "180px", paddingRight: labelValueGap }}>City:</span>
                            <span>
                              {audienceCity.length === 0 ? "-" : audienceCity.map((v, i) => <div key={i}>{v}</div>)}
                            </span>
                          </div>
                        </Col>
                      </Row>
                    </>
                  )}

                  <hr className="my-3" style={{ borderColor: "#ced4da" }} />

                  <Row className="mb-3 align-items-center g-0">
                    <Col xs="12" md="auto" style={{ width: "120px", minWidth: "120px", flex: "0 0 120px", paddingRight: labelValueGap }}>
                      <div className="fw-bold" style={{ fontSize: "1rem", whiteSpace: "nowrap" }}>
                        Broadcast Time:
                      </div>
                    </Col>
                    <Col style={{ paddingLeft: labelValueGap }}>
                      <span>{broadcastTimeText}</span>
                    </Col>
                  </Row>

                  <Row className="mt-3 mb-3 align-items-start g-0">
                    <Col xs="12" md="auto" style={{ width: "120px", minWidth: "120px", flex: "0 0 120px", paddingRight: labelValueGap }}>
                      <div className="fw-bold" style={{ fontSize: "1rem", whiteSpace: "nowrap" }}>
                        Broadcast Details:
                      </div>
                    </Col>
                    <Col style={{ paddingLeft: labelValueGap }}>
                      {contentBlocks.length === 0 ? (
                        <span>-</span>
                      ) : (
                        contentBlocks.map((block, index) => (
                          <div
                            key={block.id || index}
                            className="mb-3"
                            style={{
                              border: "1px solid #ced4da",
                              borderRadius: "8px",
                              overflow: "hidden",
                            }}
                          >
                            <div
                              style={{
                                backgroundColor: "#dee2e6",
                                padding: "0.5rem 0.75rem",
                                fontSize: "0.875rem",
                                fontWeight: 600,
                              }}
                            >
                              {isImageType(block.type) ? "Picture" : "Text"}
                            </div>
                            <div style={{ padding: "0.75rem 1rem", backgroundColor: "#fff" }}>
                              {isImageType(block.type) ? (
                                <>
                                  {block.imageUrl ? (
                                    <div className="mb-2">
                                      <img
                                        src={block.imageUrl}
                                        alt=""
                                        style={{
                                          maxWidth: "200px",
                                          maxHeight: "120px",
                                          objectFit: "contain",
                                          border: "1px solid #ced4da",
                                          borderRadius: "6px",
                                        }}
                                      />
                                    </div>
                                  ) : (
                                    <div className="text-muted small mb-2">(No image)</div>
                                  )}
                                  <Row className="mt-2">
                                    <Col md={6}>
                                      <span className="fw-semibold" style={{ color: "#495057" }}>Title Attribute : </span>
                                      <span>{block.imageTitle || "-"}</span>
                                    </Col>
                                    <Col md={6}>
                                      <span className="fw-semibold" style={{ color: "#495057" }}>Alt Attribute : </span>
                                      <span>{block.imageAlt || "-"}</span>
                                    </Col>
                                  </Row>
                                </>
                              ) : (
                                <div style={{ whiteSpace: "pre-wrap" }}>{block.textContent || "(Empty)"}</div>
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </Col>
                  </Row>
                </div>
                )}

                {/* Campaign Information */}
                <div className="pt-3" style={{ borderTop: "1px solid #ced4da" }}>
                  <div className="mb-2 fw-bold" style={{ fontSize: "1rem", color: "#495057" }}>
                    Campaign Information
                  </div>
                  <Row className="mb-2">
                    <Label className="col-md-3 col-form-label fw-semibold">Campaign Name</Label>
                    <Col md={5}>
                      <div style={readOnlyBoxStyle}>{values.campaignName || "-"}</div>
                    </Col>
                  </Row>
                  <Row className="mb-2 align-items-center">
                    <Col md={3}>
                      <Label className="col-form-label fw-semibold">Start Date</Label>
                      <div style={readOnlyBoxStyle}>
                        {values.startDate ? moment(values.startDate).format("YYYY-MM-DD") : "-"}
                      </div>
                    </Col>
                    <Col md={3}>
                      <Label className="col-form-label fw-semibold">End Date</Label>
                      <div style={readOnlyBoxStyle}>
                        {values.endDate ? moment(values.endDate).format("YYYY-MM-DD") : "-"}
                      </div>
                    </Col>
                  </Row>
                </div>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>
      {/* UID Viewer Modal */}
      <Modal isOpen={showUidModal} toggle={() => setShowUidModal(false)} centered size="md">
        <ModalHeader toggle={() => setShowUidModal(false)}>
          Specific User UIDs ({(values.specificUserLineUids || []).length} total)
        </ModalHeader>
        <ModalBody>
          {(() => {
            const uids = values.specificUserLineUids || []
            const totalPages = Math.ceil(uids.length / UIDS_PER_PAGE) || 1
            const startIdx = (uidPage - 1) * UIDS_PER_PAGE
            const pageUids = uids.slice(startIdx, startIdx + UIDS_PER_PAGE)

            return (
              <>
                <table className="table table-bordered table-sm mb-3">
                  <thead style={{ backgroundColor: "#f8f9fa" }}>
                    <tr>
                      <th style={{ width: "60px", textAlign: "center" }}>#</th>
                      <th>LINE UID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageUids.length === 0 ? (
                      <tr>
                        <td colSpan={2} className="text-center text-muted">No data</td>
                      </tr>
                    ) : (
                      pageUids.map((uid, i) => (
                        <tr key={startIdx + i}>
                          <td style={{ textAlign: "center" }}>{startIdx + i + 1}</td>
                          <td style={{ wordBreak: "break-all" }}>{uid}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>

                {totalPages > 1 && (
                  <div className="d-flex justify-content-between align-items-center">
                    <small className="text-muted">
                      Page {uidPage} / {totalPages}
                    </small>
                    <div className="d-flex gap-1">
                      <Button
                        size="sm"
                        outline
                        color="secondary"
                        disabled={uidPage <= 1}
                        onClick={() => setUidPage(p => p - 1)}
                      >
                        <i className="bx bx-chevron-left"></i>
                      </Button>
                      <Button
                        size="sm"
                        outline
                        color="secondary"
                        disabled={uidPage >= totalPages}
                        onClick={() => setUidPage(p => p + 1)}
                      >
                        <i className="bx bx-chevron-right"></i>
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )
          })()}
        </ModalBody>
      </Modal>
    </>
  )
}

Step2CampaignDetails.propTypes = {
  validation: PropTypes.object.isRequired,
  pageView: PropTypes.string,
  surveyName: PropTypes.string,
  categoryOptions: PropTypes.array,
  lineOaOptions: PropTypes.array,
}

export default Step2CampaignDetails
